// Shared rank/tier system — mirrors backend RANK_TIERS in server.py
export const RANK_TIERS = [
    { key: "BRONZE",   label: "Bronze",   min: 0,     color: "#B87333", glow: "rgba(184,115,51,0.35)" },
    { key: "SILVER",   label: "Silver",   min: 3000,  color: "#BFC1C2", glow: "rgba(191,193,194,0.35)" },
    { key: "GOLD",     label: "Gold",     min: 6000,  color: "#F5C518", glow: "rgba(245,197,24,0.35)" },
    { key: "PLATINUM", label: "Platinum", min: 10000, color: "#67E8F9", glow: "rgba(103,232,249,0.35)" },
    { key: "DIAMOND",  label: "Diamond",  min: 15000, color: "#B9F2FF", glow: "rgba(185,242,255,0.4)" },
    { key: "CHAMPION", label: "Champion", min: 20000, color: "#A855F7", glow: "rgba(168,85,247,0.5)" },
];

export function getRank(elo) {
    const n = Number(elo) || 0;
    let current = RANK_TIERS[0];
    let idx = 0;
    for (let i = 0; i < RANK_TIERS.length; i++) {
        if (n >= RANK_TIERS[i].min) { current = RANK_TIERS[i]; idx = i; }
        else break;
    }
    const next = RANK_TIERS[idx + 1] || null;
    const spanStart = current.min;
    const spanEnd = next ? next.min : n + 1;
    const progress = next ? Math.max(0, Math.min(1, (n - spanStart) / Math.max(1, spanEnd - spanStart))) : 1;
    return { ...current, tier_index: idx, next_min: next?.min ?? null, progress };
}

// Tiny inline badge — good for leaderboard rows, team cards, roster lines
export const RankBadge = ({ elo, size = "sm", showElo = false, className = "", testId }) => {
    const r = getRank(elo);
    const dot = { xs: 6, sm: 8, md: 10 }[size] || 8;
    const px = { xs: "text-[9px] px-1.5 py-0.5", sm: "text-[10px] px-2 py-0.5", md: "text-[11px] px-2.5 py-1" }[size];
    return (
        <span
            data-testid={testId}
            title={`${r.label} · ${elo} ELO`}
            className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.2em] border ${px} ${className}`}
            style={{ borderColor: r.color + "80", color: r.color, backgroundColor: r.color + "14" }}
        >
            <span className="rounded-full" style={{ width: dot, height: dot, background: r.color, boxShadow: `0 0 8px ${r.glow}` }} />
            <span>{r.label}</span>
            {showElo && <span className="opacity-70">· {elo}</span>}
        </span>
    );
};

// Full-width mini progress bar → used in header
export const RankProgress = ({ elo, className = "", testId }) => {
    const r = getRank(elo);
    return (
        <div className={`w-full ${className}`} data-testid={testId}>
            <div className="h-1 bg-white/8 overflow-hidden rounded-sm">
                <div className="h-full transition-all" style={{ width: `${Math.round(r.progress * 100)}%`, background: r.color, boxShadow: `0 0 6px ${r.glow}` }} />
            </div>
            {r.next_min !== null && (
                <div className="mt-1 flex items-center justify-between font-mono text-[9px] tracking-[0.22em] uppercase text-white/40">
                    <span>{elo}</span>
                    <span>{r.next_min}</span>
                </div>
            )}
        </div>
    );
};

// Big showpiece badge — used on player profile / team card hero
export const RankPlaque = ({ elo, testId, subtitle }) => {
    const r = getRank(elo);
    return (
        <div className="border p-4 bg-[#050505]" style={{ borderColor: r.color + "55" }} data-testid={testId}>
            <div className="flex items-center gap-3">
                <div
                    className="w-10 h-10 flex items-center justify-center font-display font-black text-lg"
                    style={{ background: r.color, color: "#0A0A0A", boxShadow: `0 0 20px ${r.glow}` }}
                >
                    {r.label.charAt(0)}
                </div>
                <div className="min-w-0">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Rank</div>
                    <div className="font-display font-black text-lg leading-tight" style={{ color: r.color }}>{r.label}</div>
                </div>
            </div>
            {r.next_min !== null && (
                <div className="mt-3">
                    <div className="h-1 bg-white/8 overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${Math.round(r.progress * 100)}%`, background: r.color }} />
                    </div>
                    <div className="mt-1 font-mono text-[9px] tracking-[0.22em] uppercase text-white/40 flex justify-between">
                        <span>{elo}</span>
                        <span>{r.next_min}</span>
                    </div>
                </div>
            )}
            {subtitle && <div className="mt-2 font-mono text-[9px] tracking-[0.22em] uppercase text-white/50">{subtitle}</div>}
        </div>
    );
};
